import { ProductType } from "../products";

import { findIndex, sortBy, filter, pick, remove, pickBy } from "lodash";
import { Application, ApplicationManufacturer } from "./_models";
import { IWebPhase0Model, IWebApplicationAssessment, IWebApplicationTest } from "./_webmodels";
import { applicationInitValues, initWebApplicationManufacturerValues } from "./_constants";
import { array_fill_keys, isPopulatedArray } from "../../../components/helpers";

export const generateSchemesCategories = (productType: any, productTypes: Array<ProductType>) => {
  let _schemes: any = [];
  let _categories: any = [];
  let checklistFileId = 0;

  if (! (productType && productTypes && findIndex(productTypes, { id :parseInt(`${productType}`)}) > -1)) {
    return { categories: _categories, schemes: _schemes, checklistFileId }
  }

  const _idx = findIndex(productTypes, { id :parseInt(productType +'')} );
  const ptype = productTypes[_idx];

  // set schemes
  if(ptype?.schemes && isPopulatedArray(ptype?.schemes)) {
    _schemes = filter(ptype.schemes, o => (! o?.deleted)).map(o => pick(o, ['id', 'name', 'description', 'assessmentRequired']));
    const _other = remove(_schemes, (o: any) => (o.name.toLowerCase() === 'others' || o.name.toLowerCase() === 'other'))
    _schemes = sortBy(_schemes, ['name']);
    if (_other) {
      _schemes.push(..._other);
    }
  }

  // set categories
  if (ptype?.productCategoriesSet && isPopulatedArray(ptype.productCategoriesSet)) {

    _categories = filter(ptype.productCategoriesSet, o => (! o?.deleted)).map(o => pick(o, ['id', 'name']));
    const _other = remove(_categories, (o: any) => (o.name.toLowerCase() === 'others' || o.name.toLowerCase() === 'other'));
    _categories = sortBy(_categories, ['name']);
    if (_other) {
      _categories.push(..._other);
    }
  }

  // set checklist file id
  if (ptype?.checklistFile && ptype.checklistFile?.id) checklistFileId = ptype.checklistFile.id;

  return { categories: _categories, schemes: _schemes, checklistFileId }
}

const APP_FIELDS = [

  // numeric fields
  'id',
  // string fields
  'applicationType',
  'companyName',
  'companyAddress',
  'companyPerson',
  'companyPersonDesignation',
  'companyTel',
  'companyFax',
  'companyEmail',
  'companyWebsite',
  'productCategoryString',
  'productBrand',
  'productModel',
  'productOrigin',
  'productDetails',
  'note',
  'comment',

  // boolean
  'transfer',
];

const APP_MANUFACTURER_FIELDS = [
  'manufacturerName',
  'manufacturerAddress',
  'manufacturerPerson',
  'manufacturerPersonDesignation',
  'manufacturerTel',
  'manufacturerFax',
  'manufacturerEmail',
  'manufacturerWebsite',
];

const APP_ID_FIELDS = [
  // id
  'fromCertificate',
  'productType',
  'productCategory',
  'scheme',
  // ==> 'schemeName'
];

const APP_ATTACHMENT_FIELDS = [
  // attachments
  'companyAcraAttachment',
  'transferAttachment',
];

const extractFileIDName = (obj: any, field: string) => {

  if (! (obj && field && Object.hasOwn(obj, field) && obj[field]?.id)) return {};
  return {
    [field] : obj[field].id ?? 0,
    [`${field}Name`] : obj[field]?.fileName ?? ''
  };
}


export const appToSubmit = (appData: Application) : IWebPhase0Model => {

  let _payload = {
    ...applicationInitValues,
    companyId : appData?.company?.id ? appData.company.id : 0,
  };

  if (! appData) return _payload;

  // straight assignment
  APP_FIELDS.forEach(fld => {
    //@ts-ignore
    if (appData.hasOwnProperty(fld) && appData[fld]) {
      //@ts-ignore
      Object.assign(_payload, {[fld]: appData[fld]})
    }
  })

  APP_ID_FIELDS.forEach(fld => {
    //@ts-ignore
    if (appData.hasOwnProperty(fld) && appData[fld] && appData[fld]?.id) {
      //@ts-ignore
      Object.assign(_payload, {[fld]: appData[fld].id})
      // set schemeName
      if (fld === 'scheme' && appData.scheme?.name) {
        Object.assign(_payload, {schemeName: appData.scheme.name})
      }
    }
  })

  APP_ATTACHMENT_FIELDS.forEach(fld => {
    //@ts-ignore
    if (appData.hasOwnProperty(fld) && appData[fld] && appData[fld]?.id) {
      //@ts-ignore
      Object.assign(_payload, {[fld]: appData[fld].id, [`${fld}Name`]: appData[fld]?.fileName ? appData[fld].fileName : ''})
    }
  })

  if (appData?.isoAttachment && isPopulatedArray(appData?.isoAttachment)) {
    Object.assign(_payload, {
      isoFileAttachments: filter(appData.isoAttachment, (o) => (! o.deleted)).map(o => ({id: o.id, name: o.fileName}))
    })
  }
  if (appData?.productAttachment && isPopulatedArray(appData?.productAttachment)) {
    Object.assign(_payload, {
      productFileAttachments: filter(appData.productAttachment, (o) => (! o.deleted)).map(o => ({id: o.id, name: o.fileName}))
    })
  }

  if (appData?.tests && isPopulatedArray(appData.tests)) {
    let _testbase = {
      ...array_fill_keys(['id','testAttach','testAuthAttach'],0),
      ...array_fill_keys(['testReferenceNumber','testReportDate','testStandards','testConductedDate','testLocation','testResult','testAttachName','testAuthAttachName'],''),
    };

    Object.assign(_payload, {
      tests : filter(appData.tests, o => (! o?.deleted)).map( file => ({
        ..._testbase,
        ...pick(file, ['id','testReferenceNumber','testReportDate','testStandards','testConductedDate','testLocation','testResult']),
        ...extractFileIDName(file, 'testAttach'),
        ...extractFileIDName(file, 'testAuthAttach'),        
      }))
    })
  }

  if (appData?.assessments && isPopulatedArray(appData.assessments)) {
    let _assessbase = {
      ...array_fill_keys(['id','testAttach','testAuthAttach'],0),
      ...array_fill_keys(['testReferenceNumber','testReportDate','testStandards','testConductedDate','testLocation','testResult','testAttachName','testAuthAttachName'],''),
    };

    Object.assign(_payload, {
      assessments : filter(appData.assessments, o => (! o?.deleted)).map( file => ({
        ..._assessbase,
        ...pick(file, ['id','testReferenceNumber','testReportDate','testStandards','testConductedDate','testLocation','testResult']),
        ...extractFileIDName(file, 'testAttach'),
        ...extractFileIDName(file, 'testAuthAttach'),        
      }))
    })
  }

  //  add manufacturer details from array
  if (appData?.manufacturers && isPopulatedArray(appData.manufacturers)) {
    Object.assign(_payload, {
      manufacturers: filter(appData.manufacturers, (o) => (! o.deleted)).map((o : ApplicationManufacturer) => ({
        ...initWebApplicationManufacturerValues,
        ...pick(o, ['id','manufacturerName','manufacturerAddress','manufacturerPerson','manufacturerPersonDesignation','manufacturerTel','manufacturerFax','manufacturerEmail','manufacturerWebsite']),
        ...((o?.manufacturerLetterAttachment && o.manufacturerLetterAttachment?.id && (! o.manufacturerLetterAttachment?.deleted)) ? {manufacturerLetterAttachment : o.manufacturerLetterAttachment.id, manufacturerLetterAttachmentAttachmentName: o.manufacturerLetterAttachment.fileName} : {})
      }))
    })
  } else if (appData.hasOwnProperty('manufacturerName')) {
    // try to load previous model
    let manufacturer = {
      ...initWebApplicationManufacturerValues,
      ...pick(appData, APP_MANUFACTURER_FIELDS),
    }
    if (appData.hasOwnProperty('manufacturerLetterAttachment')) {
      // @ts-ignore
      Object.assign(manufacturer, {manufacturerLetterAttachment : appData.manufacturerLetterAttachment.id, manufacturerLetterAttachmentAttachmentName: appData.manufacturerLetterAttachment.fileName});
    }

    Object.assign(_payload, { manufacturers: [ manufacturer ] });
  }

  return _payload;
}

export const cleanApplicationReports = (list: Array<IWebApplicationTest | IWebApplicationAssessment>) => {
  if (! (list && isPopulatedArray(list))) return [];

  return list.map(report => pickBy(report, (value, key) => (key === 'id' || (value && (! key.match(/attachmentName$/i))))));
}
